import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllStandardFoods,
  getSingleStandardFood,
  createStandardFood,
  updateStandardFood,
  deleteStandardFood,
} from "@/api/new-api/staff-management/menu-manage/standard-food";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks

export const fetchAllStandardFoods = createAsyncThunk(
  "standardFoods/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllStandardFoods(restaurant_id);
    if (res.status === "success") return res.data.standard_foods;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleStandardFood = createAsyncThunk(
  "standardFoods/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleStandardFood(id);
    if (res.status === "success") return res.data.standard_food;
    return rejectWithValue(res.message);
  }
);

export const createNewStandardFood = createAsyncThunk(
  "standardFoods/create",
  async (data, { rejectWithValue }) => {
    const res = await createStandardFood(data);
    if (res.status === "success") return res.data.standard_food;
    return rejectWithValue(res.message);
  }
);

export const updateStandardFoodById = createAsyncThunk(
  "standardFoods/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateStandardFood(id, data);
    if (res.status === "success") return res.data.standard_food;
    return rejectWithValue(res.message);
  }
);

export const deleteStandardFoodById = createAsyncThunk(
  "standardFoods/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteStandardFood(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updateStandardFoodToggle = createAsyncThunk("standardFoodsToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial State

const initialState = {
  standardFoods: [],
  singleStandardFood: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const standardFoodSlice = createSlice({
  name: "standardFoods",
  initialState,
  reducers: {
    clearStandardFoodMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllStandardFoods.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllStandardFoods.fulfilled, (state, action) => {
        state.getLoading = false;
        state.standardFoods = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllStandardFoods.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleStandardFood.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleStandardFood.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleStandardFood = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleStandardFood.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewStandardFood.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewStandardFood.fulfilled, (state, action) => {
        state.createLoading = false;
        state.standardFoods.push(action.payload);
        state.success = "Standard food created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewStandardFood.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateStandardFoodById.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateStandardFoodById.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.standardFoods = state.standardFoods.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
        state.success = "Standard food updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateStandardFoodById.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteStandardFoodById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteStandardFoodById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.standardFoods = state.standardFoods.filter(
          (item) => item.id !== action.payload
        );
        state.success = "Standard food deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteStandardFoodById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateStandardFoodToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateStandardFoodToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.standardFoods = state.standardFoods.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateStandardFoodToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
      
  },
});

export const { clearStandardFoodMessages } = standardFoodSlice.actions;
export default standardFoodSlice.reducer;
